(function () {
  'use strict';

  if (window.TIBGuidePlayerBootstrapped) return;
  window.TIBGuidePlayerBootstrapped = true;

  const registry = new Map();

  class TIBGuidePlayer {
    constructor(root) {
      this.root = root;
      this.sectionId = root.dataset.sectionId;
      this.steps = Array.from(root.querySelectorAll('[data-guide-step]'));
      this.stepButtons = Array.from(root.querySelectorAll('[data-guide-step-button]'));
      this.currentLabel = root.querySelector('[data-guide-current]');
      this.totalLabel = root.querySelector('[data-guide-total]');
      this.totalBadge = root.querySelector('[data-guide-total-badge]');
      this.progress = root.querySelector('[data-guide-progress]');
      this.previousButton = root.querySelector('[data-guide-footer-previous]');
      this.nextButton = root.querySelector('[data-guide-footer-next]');
      this.browser = root.querySelector('[data-guide-browser]');
      this.browserBar = root.querySelector('[data-guide-browser-bar]');
      this.browserCloseButton = root.querySelector('[data-guide-browser-close]');
      this.browserPreviousButton = root.querySelector('[data-guide-browser-previous]');
      this.browserNextButton = root.querySelector('[data-guide-browser-next]');
      this.browserRestartButton = root.querySelector('[data-guide-browser-restart]');
      this.browserCopyButton = root.querySelector('[data-guide-browser-copy]');
      this.browserFullscreenButton = root.querySelector('[data-guide-browser-fullscreen]');
      this.stage = root.querySelector('[data-guide-stage]');
      this.baseWidth = Math.max(1, Number(this.browser && this.browser.dataset.guideBaseWidth) || 1600);
      this.baseHeight = Math.max(1, Number(this.browser && this.browser.dataset.guideBaseHeight) || 900);
      this.fallbackFullscreen = false;
      this.bodyOverflowBeforeFullscreen = '';
      this.copyFeedbackTimer = null;
      this.designMode = root.dataset.designMode === 'true';
      this.allowStepNavigation = false;
      this.rememberProgress = root.dataset.rememberProgress === 'true';
      this.currentIndex = 0;
      this.activationToken = 0;
      this.timers = new Map();
      this.cleanups = [];
      this.labelLayoutFrame = null;
      this.labelLayoutTimers = new Set();
      this.previewLayoutFrame = null;
      this.previewMeasureNode = null;
      this.mediaLoadPromises = new WeakMap();
      this.gifAssetPromises = new Map();
      this.gifRunCounter = 0;
      this.objectUrls = new Set();
      this.preloadGeneration = 0;
      this.memoryState = { fields: {}, latestKey: '', latestValue: '', currentStep: 0, previewBoxes: {}, latestPreviewBox: null };
      this.storage = this.getStorage();
      this.storageKey = this.getStorageKey();
      this.state = this.loadState();

      this.applySectionSettings();
      this.bindEvents();
      this.hydrateInputs();
      this.hydratePreviewBoxes();
      this.bindPreviewResizeHandles();
      this.bindPreviewFrameDragging();

      const requestedInitial = Math.max(0, Number(root.dataset.initialStep || 1) - 1);
      const remembered = this.rememberProgress ? Number(this.state.currentStep || 0) : requestedInitial;
      this.showStep(this.clampIndex(remembered), { focus: false, persist: false });
      this.preloadMediaSequentially();
    }

    applySectionSettings() {
      const showTotalBadge = this.root.dataset.showTotalBadge === 'true';
      this.allowStepNavigation = this.root.dataset.allowStepNavigation === 'true';

      if (this.totalBadge) {
        this.totalBadge.hidden = !showTotalBadge;
        this.totalBadge.style.display = showTotalBadge ? '' : 'none';
      }

      this.stepButtons.forEach((button) => {
        button.setAttribute('aria-disabled', this.allowStepNavigation ? 'false' : 'true');
        button.tabIndex = this.allowStepNavigation ? 0 : -1;
      });
    }

    getStorage() {
      try {
        if (this.root.dataset.storageMode === 'local') return window.localStorage;
        if (this.root.dataset.storageMode === 'session') return window.sessionStorage;
      } catch (error) {
        return null;
      }
      return null;
    }

    getStorageKey() {
      const customKey = (this.root.dataset.storageKey || '').trim();
      return `tib-guide:${customKey || this.sectionId}`;
    }

    loadState() {
      if (!this.storage) return this.memoryState;

      try {
        const parsed = JSON.parse(this.storage.getItem(this.storageKey) || '{}');
        return {
          fields: parsed.fields && typeof parsed.fields === 'object' ? parsed.fields : {},
          latestKey: parsed.latestKey || '',
          latestValue: parsed.latestValue || '',
          currentStep: Number(parsed.currentStep || 0),
          previewBoxes: parsed.previewBoxes && typeof parsed.previewBoxes === 'object' ? parsed.previewBoxes : {},
          latestPreviewBox: parsed.latestPreviewBox && typeof parsed.latestPreviewBox === 'object'
            ? parsed.latestPreviewBox
            : (
                parsed.latestPreviewPosition && typeof parsed.latestPreviewPosition === 'object'
                  ? {
                      x: parsed.latestPreviewPosition.x,
                      y: parsed.latestPreviewPosition.y
                    }
                  : null
              )
        };
      } catch (error) {
        return this.memoryState;
      }
    }

    saveState() {
      this.memoryState = this.state;
      if (!this.storage) return;

      try {
        this.storage.setItem(this.storageKey, JSON.stringify(this.state));
      } catch (error) {
        // Storage can be blocked by browser privacy settings.
      }
    }

    bindEvents() {
      this.stepButtons.forEach((button) => {
        const handler = () => {
          if (this.root.dataset.allowStepNavigation !== 'true') return;
          this.showStep(Number(button.dataset.stepIndex), { focus: true });
        };
        button.addEventListener('click', handler);
        this.cleanups.push(() => button.removeEventListener('click', handler));
      });

      this.root.querySelectorAll('[data-guide-next]').forEach((button) => {
        const handler = () => this.next(false);
        button.addEventListener('click', handler);
        this.cleanups.push(() => button.removeEventListener('click', handler));
      });

      this.root.querySelectorAll('[data-guide-previous]').forEach((button) => {
        const handler = () => this.previous();
        button.addEventListener('click', handler);
        this.cleanups.push(() => button.removeEventListener('click', handler));
      });

      this.root.querySelectorAll('[data-guide-hotspot]').forEach((button) => {
        const handler = () => this.next(false);
        button.addEventListener('click', handler);
        this.cleanups.push(() => button.removeEventListener('click', handler));
      });

      this.root.querySelectorAll('[data-guide-input]').forEach((input) => {
        const inputHandler = () => this.handleInput(input);
        const keyHandler = (event) => {
          if (event.key !== 'Enter' || input.dataset.nextOnEnter !== 'true') return;
          event.preventDefault();
          this.next(false);
        };

        input.addEventListener('input', inputHandler);
        input.addEventListener('keydown', keyHandler);
        this.cleanups.push(() => input.removeEventListener('input', inputHandler));
        this.cleanups.push(() => input.removeEventListener('keydown', keyHandler));
      });

      if (this.previousButton) {
        const handler = () => this.previous();
        this.previousButton.addEventListener('click', handler);
        this.cleanups.push(() => this.previousButton.removeEventListener('click', handler));
      }

      if (this.nextButton) {
        const handler = () => {
          const step = this.steps[this.currentIndex];
          const gifState = step ? step.dataset.gifInteractionState : '';

          if (
            this.isLastStep() &&
            (!this.isGifInteractBeforePlayStep(step) || gifState === 'done')
          ) {
            this.restart();
            return;
          }

          this.next(false);
        };
        this.nextButton.addEventListener('click', handler);
        this.cleanups.push(() => this.nextButton.removeEventListener('click', handler));
      }

      if (this.browserPreviousButton) {
        const handler = () => this.previous();
        this.browserPreviousButton.addEventListener('click', handler);
        this.cleanups.push(() => this.browserPreviousButton.removeEventListener('click', handler));
      }

      if (this.browserNextButton) {
        const handler = () => this.next(false);
        this.browserNextButton.addEventListener('click', handler);
        this.cleanups.push(() => this.browserNextButton.removeEventListener('click', handler));
      }

      if (this.browserRestartButton) {
        const handler = () => this.restart();
        this.browserRestartButton.addEventListener('click', handler);
        this.cleanups.push(() => this.browserRestartButton.removeEventListener('click', handler));
      }

      if (this.browserCloseButton) {
        const handler = () => this.exitBrowserFullscreen();
        this.browserCloseButton.addEventListener('click', handler);
        this.cleanups.push(() => this.browserCloseButton.removeEventListener('click', handler));
      }

      if (this.browserCopyButton) {
        const handler = () => this.copyCurrentUrl();
        this.browserCopyButton.addEventListener('click', handler);
        this.cleanups.push(() => this.browserCopyButton.removeEventListener('click', handler));
      }

      if (this.browserFullscreenButton) {
        const handler = () => this.toggleBrowserFullscreen();
        this.browserFullscreenButton.addEventListener('click', handler);
        this.cleanups.push(() => this.browserFullscreenButton.removeEventListener('click', handler));
      }

      const fullscreenHandler = () => this.handleFullscreenChange();
      document.addEventListener('fullscreenchange', fullscreenHandler);
      document.addEventListener('webkitfullscreenchange', fullscreenHandler);
      this.cleanups.push(() => document.removeEventListener('fullscreenchange', fullscreenHandler));
      this.cleanups.push(() => document.removeEventListener('webkitfullscreenchange', fullscreenHandler));

      const resizeHandler = () => {
        if (this.isBrowserFullscreen()) this.sizeFullscreenStage();
        this.requestInstructionLabelLayout();
        this.requestPreviewLayouts();
      };
      window.addEventListener('resize', resizeHandler, { passive: true });
      this.cleanups.push(() => window.removeEventListener('resize', resizeHandler));
    }

    getFullscreenElement() {
      return document.fullscreenElement || document.webkitFullscreenElement || null;
    }

    isBrowserFullscreen() {
      return Boolean(this.browser && (this.fallbackFullscreen || this.getFullscreenElement() === this.browser));
    }

    async toggleBrowserFullscreen() {
      if (this.isBrowserFullscreen()) {
        await this.exitBrowserFullscreen();
      } else {
        await this.enterBrowserFullscreen();
      }
    }

    async enterBrowserFullscreen() {
      if (!this.browser || this.isBrowserFullscreen()) return;

      const request = this.browser.requestFullscreen || this.browser.webkitRequestFullscreen;
      if (request) {
        try {
          const result = request.call(this.browser);
          if (result && typeof result.then === 'function') await result;
          return;
        } catch (error) {
          // Fall back to a fixed full-viewport player when the browser blocks the API.
        }
      }

      this.setFallbackFullscreen(true);
    }

    async exitBrowserFullscreen() {
      if (this.fallbackFullscreen) {
        this.setFallbackFullscreen(false);
        return;
      }

      if (this.getFullscreenElement() !== this.browser) return;
      const exit = document.exitFullscreen || document.webkitExitFullscreen;
      if (!exit) return;

      try {
        const result = exit.call(document);
        if (result && typeof result.then === 'function') await result;
      } catch (error) {
        // The fullscreen state may already have changed.
      }
    }

    setFallbackFullscreen(active) {
      if (!this.browser || this.fallbackFullscreen === active) return;
      this.fallbackFullscreen = active;
      this.browser.classList.toggle('is-fallback-fullscreen', active);

      if (active) {
        this.bodyOverflowBeforeFullscreen = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
      } else {
        document.body.style.overflow = this.bodyOverflowBeforeFullscreen;
      }

      this.handleFullscreenChange();
    }

    handleFullscreenChange() {
      if (!this.browser) return;
      const active = this.isBrowserFullscreen();
      this.browser.classList.toggle('is-fullscreen', active);

      if (this.browserCloseButton) this.browserCloseButton.disabled = !active;
      if (this.browserFullscreenButton) {
        this.browserFullscreenButton.setAttribute('aria-pressed', active ? 'true' : 'false');
        this.browserFullscreenButton.setAttribute('aria-label', active ? 'Exit fullscreen' : 'Enter fullscreen');
        this.browserFullscreenButton.setAttribute('title', active ? 'Exit fullscreen' : 'Enter fullscreen');
      }

      if (active) {
        this.sizeFullscreenStage();
      } else {
        if (this.stage) {
          this.stage.style.removeProperty('width');
          this.stage.style.removeProperty('height');
        }
        if (this.browserBar) {
          this.browserBar.style.removeProperty('width');
        }
      }

      this.scheduleInstructionLabelLayout(this.steps[this.currentIndex]);
      this.requestPreviewLayouts();
    }

    sizeFullscreenStage() {
      if (!this.browser || !this.stage || !this.isBrowserFullscreen()) return;

      const browserWidth = this.browser.clientWidth || window.innerWidth;
      const browserHeight = this.browser.clientHeight || window.innerHeight;
      const barHeight = this.browserBar ? this.browserBar.offsetHeight : 0;
      const availableWidth = Math.max(1, browserWidth);
      const availableHeight = Math.max(1, browserHeight - barHeight);
      const ratio = this.baseWidth / this.baseHeight;

      let width = availableWidth;
      let height = width / ratio;
      if (height > availableHeight) {
        height = availableHeight;
        width = height * ratio;
      }

      const renderedWidth = Math.floor(width);
      const renderedHeight = Math.floor(height);

      this.stage.style.width = `${renderedWidth}px`;
      this.stage.style.height = `${renderedHeight}px`;
      if (this.browserBar) {
        this.browserBar.style.width = `${renderedWidth}px`;
      }
      this.requestInstructionLabelLayout();
      this.requestPreviewLayouts();
    }

    async copyCurrentUrl() {
      if (!this.browserCopyButton) return;
      const url = window.location.href;
      let copied = false;

      try {
        if (navigator.clipboard && window.isSecureContext) {
          await navigator.clipboard.writeText(url);
          copied = true;
        }
      } catch (error) {
        copied = false;
      }

      if (!copied) {
        const textarea = document.createElement('textarea');
        textarea.value = url;
        textarea.setAttribute('readonly', '');
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';
        document.body.appendChild(textarea);
        textarea.select();
        try {
          copied = document.execCommand('copy');
        } catch (error) {
          copied = false;
        }
        textarea.remove();
      }

      if (!copied) return;
      this.browserCopyButton.classList.add('is-copied');
      this.browserCopyButton.setAttribute('aria-label', 'Page link copied');
      this.browserCopyButton.setAttribute('title', 'Page link copied');
      if (this.copyFeedbackTimer) window.clearTimeout(this.copyFeedbackTimer);
      this.copyFeedbackTimer = window.setTimeout(() => {
        this.copyFeedbackTimer = null;
        if (!this.browserCopyButton) return;
        this.browserCopyButton.classList.remove('is-copied');
        this.browserCopyButton.setAttribute('aria-label', 'Copy page link');
        this.browserCopyButton.setAttribute('title', 'Copy page link');
      }, 1600);
    }

    hydrateInputs() {
      let stateChanged = false;

      this.root.querySelectorAll('[data-guide-input]').forEach((input) => {
        const key = input.dataset.fieldKey || 'guide_text';
        const defaultValue = input.dataset.defaultValue || '';
        const storedValue = this.state.fields[key];
        const value = storedValue !== undefined && String(storedValue).trim()
          ? storedValue
          : defaultValue;

        input.value = value;

        if (this.state.fields[key] !== value) {
          this.state.fields[key] = value;
          stateChanged = true;
        }
      });

      if (stateChanged) this.saveState();
      this.updatePreviews();
    }

    getInputEffectiveValue(input, restoreDefault) {
      if (!input) return '';

      const currentValue = input.value || '';
      if (currentValue.trim()) return currentValue;

      const defaultValue = input.dataset.defaultValue || '';
      if (!defaultValue.trim()) return '';

      if (restoreDefault) input.value = defaultValue;
      return defaultValue;
    }

    storeInputValue(input, restoreDefault) {
      if (!input) return '';

      const key = input.dataset.fieldKey || 'guide_text';
      const value = this.getInputEffectiveValue(input, restoreDefault);
      this.state.fields[key] = value;
      this.state.latestKey = key;
      this.state.latestValue = value;
      this.saveState();
      this.updatePreviews();
      return value;
    }

    handleInput(input) {
      this.storeInputValue(input, false);
      this.updateControls();
    }

    updatePreviews() {
      this.root.querySelectorAll('[data-guide-preview]').forEach((preview) => {
        const source = preview.dataset.previewSource;
        const step = preview.closest('[data-guide-step]');
        let value = '';

        if (source === 'manual') {
          value = preview.dataset.previewManual || '';
        } else if (source === 'latest') {
          value = this.state.latestValue || '';
        } else if (source === 'current_input') {
          const input = step ? step.querySelector('[data-guide-input]') : null;
          const key = input ? input.dataset.fieldKey : preview.dataset.previewKey;
          value = (input && input.value) || this.state.fields[key] || '';
        } else {
          value = this.state.fields[preview.dataset.previewKey || 'guide_text'] || '';
        }

        preview.textContent = value;
      });

      this.root.querySelectorAll('[data-guide-inherited-input]').forEach((input) => {
        const key = input.dataset.fieldKey || 'guide_text';
        input.value = this.state.fields[key] || '';
      });

      this.requestPreviewLayouts();
    }

    hydratePreviewBoxes() {
      if (this.designMode) return;

      this.root.querySelectorAll('[data-guide-preview-frame]').forEach((frame) => {
        this.applyPreviewPositionSource(frame);
      });
    }

    getPreviewDefaultBox(frame) {
      return {
        x: Number(frame.dataset.previewDefaultX || 0),
        y: Number(frame.dataset.previewDefaultY || 0),
        width: Number(frame.dataset.previewDefaultWidth || 0),
        height: Number(frame.dataset.previewDefaultHeight || 0)
      };
    }

    setPreviewStorageBox(frame, box) {
      if (!frame || !box) return;

      frame.dataset.previewStorageX = String(Number(box.x || 0));
      frame.dataset.previewStorageY = String(Number(box.y || 0));
      frame.dataset.previewStorageWidth = String(Number(box.width || 0));
      frame.dataset.previewStorageHeight = String(Number(box.height || 0));
    }

    getPreviewStorageBox(frame) {
      if (!frame) return null;

      const hasStoredBase =
        frame.dataset.previewStorageX !== undefined &&
        frame.dataset.previewStorageY !== undefined &&
        frame.dataset.previewStorageWidth !== undefined &&
        frame.dataset.previewStorageHeight !== undefined;

      if (!hasStoredBase) return this.getPreviewBox(frame);

      return {
        x: Number(frame.dataset.previewStorageX || 0),
        y: Number(frame.dataset.previewStorageY || 0),
        width: Number(frame.dataset.previewStorageWidth || 0),
        height: Number(frame.dataset.previewStorageHeight || 0)
      };
    }

    applyPreviewPositionSource(frame) {
      if (!frame) return;

      const fallbackBox = this.getPreviewDefaultBox(frame);
      const inherit = frame.dataset.previewInheritPosition === 'true';

      if (!inherit) {
        this.setPreviewStorageBox(frame, fallbackBox);
        this.applyPreviewBox(frame, fallbackBox);
        return;
      }

      const storedBox = !this.designMode && this.state.latestPreviewBox
        ? this.state.latestPreviewBox
        : null;

      const baseBox = {
        x: storedBox && Number.isFinite(Number(storedBox.x)) ? Number(storedBox.x) : fallbackBox.x,
        y: storedBox && Number.isFinite(Number(storedBox.y)) ? Number(storedBox.y) : fallbackBox.y,
        width: storedBox && Number.isFinite(Number(storedBox.width)) ? Number(storedBox.width) : fallbackBox.width,
        height: storedBox && Number.isFinite(Number(storedBox.height)) ? Number(storedBox.height) : fallbackBox.height
      };

      this.setPreviewStorageBox(frame, baseBox);

      const baseWidth = this.clampValue(baseBox.width, 20, 1000);
      const baseHeight = this.clampValue(baseBox.height, 20, 1000);
      const requestedScale = Math.max(0.1, Number(frame.dataset.previewInheritScale || 1));
      const maximumScale = Math.min(1000 / baseWidth, 1000 / baseHeight);
      const scale = Math.min(requestedScale, maximumScale);

      const width = Math.max(20, baseWidth * scale);
      const height = Math.max(20, baseHeight * scale);
      const translateX = Number(frame.dataset.previewInheritTranslateX || 0) * 10;
      const translateY = Number(frame.dataset.previewInheritTranslateY || 0) * 10;

      const baseCenterX = baseBox.x + (baseWidth / 2);
      const baseCenterY = baseBox.y + (baseHeight / 2);
      const centerX = this.clampValue(baseCenterX + translateX, width / 2, 1000 - (width / 2));
      const centerY = this.clampValue(baseCenterY + translateY, height / 2, 1000 - (height / 2));

      this.applyPreviewBox(frame, {
        x: centerX - (width / 2),
        y: centerY - (height / 2),
        width,
        height
      });
    }

    rememberPreviewBox(step) {
      if (this.designMode || !step) return;

      const frame = step.querySelector('[data-guide-preview-frame]');
      if (!frame) return;

      const box = this.getPreviewStorageBox(frame);
      if (!box) return;

      this.state.latestPreviewBox = {
        x: box.x,
        y: box.y,
        width: box.width,
        height: box.height
      };
      this.saveState();
    }

    bindPreviewResizeHandles() {
      this.root.querySelectorAll('[data-guide-preview-handle]').forEach((handle) => {
        const pointerDown = (event) => {
          const frame = handle.closest('[data-guide-preview-frame]');
          if (!frame || !this.canResizePreviewFrame(frame)) return;

          event.preventDefault();
          event.stopPropagation();

          const stage = frame.closest('[data-guide-stage]') || this.stage;
          if (!stage) return;

          const stageRect = stage.getBoundingClientRect();
          if (!stageRect.width || !stageRect.height) return;

          const direction = handle.dataset.guidePreviewHandle || '';
          const startX = event.clientX;
          const startY = event.clientY;
          const startBox = this.getPreviewBox(frame);
          const readout = frame.querySelector('[data-guide-preview-readout]');

          frame.classList.add('is-resizing');
          if (readout) readout.hidden = false;

          const move = (moveEvent) => {
            moveEvent.preventDefault();

            const dx = (moveEvent.clientX - startX) / stageRect.width * 1000;
            const dy = (moveEvent.clientY - startY) / stageRect.height * 1000;
            const minimum = 20;

            let left = startBox.x;
            let top = startBox.y;
            let right = startBox.x + startBox.width;
            let bottom = startBox.y + startBox.height;

            if (direction.includes('w')) left += dx;
            if (direction.includes('e')) right += dx;
            if (direction.includes('n')) top += dy;
            if (direction.includes('s')) bottom += dy;

            left = this.clampValue(left, 0, 1000 - minimum);
            top = this.clampValue(top, 0, 1000 - minimum);
            right = this.clampValue(right, left + minimum, 1000);
            bottom = this.clampValue(bottom, top + minimum, 1000);

            const box = {
              x: Math.round(left),
              y: Math.round(top),
              width: Math.round(right - left),
              height: Math.round(bottom - top)
            };

            this.applyPreviewBox(frame, box);
            this.updatePreviewReadout(frame, box);
            this.requestPreviewLayouts(frame);
          };

          const end = () => {
            window.removeEventListener('pointermove', move);
            window.removeEventListener('pointerup', end);
            window.removeEventListener('pointercancel', end);
            frame.classList.remove('is-resizing');

            const box = this.getPreviewBox(frame);
            this.updatePreviewReadout(frame, box);

            if (!this.designMode && frame.dataset.previewStorefrontResize === 'true') {
              this.setPreviewStorageBox(frame, box);
              this.state.latestPreviewBox = {
                x: box.x,
                y: box.y,
                width: box.width,
                height: box.height
              };
              this.saveState();
            }

            if (readout) {
              window.setTimeout(() => {
                if (!frame.classList.contains('is-resizing')) readout.hidden = true;
              }, 900);
            }
          };

          window.addEventListener('pointermove', move, { passive: false });
          window.addEventListener('pointerup', end, { once: true });
          window.addEventListener('pointercancel', end, { once: true });
        };

        handle.addEventListener('pointerdown', pointerDown);
        this.cleanups.push(() => handle.removeEventListener('pointerdown', pointerDown));
      });
    }

    bindPreviewFrameDragging() {
      this.root.querySelectorAll('[data-guide-preview-frame]').forEach((frame) => {
        if (this.designMode || frame.dataset.previewStorefrontResize !== 'true') return;

        frame.style.pointerEvents = 'auto';
        frame.style.cursor = 'move';
        frame.style.touchAction = 'none';

        const pointerDown = (event) => {
          if (
            event.button !== 0 ||
            event.target.closest('[data-guide-preview-handle]')
          ) return;

          const step = frame.closest('[data-guide-step]');
          if (!step || step.hidden || step !== this.steps[this.currentIndex]) return;

          const stage = frame.closest('[data-guide-stage]') || this.stage;
          if (!stage) return;

          const stageRect = stage.getBoundingClientRect();
          if (!stageRect.width || !stageRect.height) return;

          event.preventDefault();
          event.stopPropagation();

          const startX = event.clientX;
          const startY = event.clientY;
          const startBox = this.getPreviewBox(frame);
          const readout = frame.querySelector('[data-guide-preview-readout]');

          frame.classList.add('is-dragging');
          frame.style.cursor = 'grabbing';
          if (readout) readout.hidden = false;

          const move = (moveEvent) => {
            moveEvent.preventDefault();

            const dx = (moveEvent.clientX - startX) / stageRect.width * 1000;
            const dy = (moveEvent.clientY - startY) / stageRect.height * 1000;

            const box = {
              x: Math.round(this.clampValue(startBox.x + dx, 0, 1000 - startBox.width)),
              y: Math.round(this.clampValue(startBox.y + dy, 0, 1000 - startBox.height)),
              width: startBox.width,
              height: startBox.height
            };

            this.applyPreviewBox(frame, box);
            this.updatePreviewReadout(frame, box);
            this.requestPreviewLayouts(frame);
          };

          const end = () => {
            window.removeEventListener('pointermove', move);
            window.removeEventListener('pointerup', end);
            window.removeEventListener('pointercancel', end);

            frame.classList.remove('is-dragging');
            frame.style.cursor = 'move';

            const box = this.getPreviewBox(frame);
            this.setPreviewStorageBox(frame, box);
            this.state.latestPreviewBox = {
              x: box.x,
              y: box.y,
              width: box.width,
              height: box.height
            };
            this.saveState();

            if (readout) {
              window.setTimeout(() => {
                if (!frame.classList.contains('is-dragging')) readout.hidden = true;
              }, 900);
            }
          };

          window.addEventListener('pointermove', move, { passive: false });
          window.addEventListener('pointerup', end, { once: true });
          window.addEventListener('pointercancel', end, { once: true });
        };

        frame.addEventListener('pointerdown', pointerDown);
        this.cleanups.push(() => {
          frame.removeEventListener('pointerdown', pointerDown);
          frame.style.removeProperty('pointer-events');
          frame.style.removeProperty('cursor');
          frame.style.removeProperty('touch-action');
        });
      });
    }

    canResizePreviewFrame(frame) {
      return Boolean(
        frame &&
        (this.designMode || frame.dataset.previewStorefrontResize === 'true')
      );
    }

    getPreviewBox(frame) {
      return {
        x: Number(frame.dataset.previewX || 0),
        y: Number(frame.dataset.previewY || 0),
        width: Number(frame.dataset.previewWidth || 0),
        height: Number(frame.dataset.previewHeight || 0)
      };
    }

    applyPreviewBox(frame, box) {
      if (!frame || !box) return;

      const x = this.clampValue(Number(box.x || 0), 0, 1000);
      const y = this.clampValue(Number(box.y || 0), 0, 1000);
      const width = this.clampValue(Number(box.width || 20), 20, 1000 - x);
      const height = this.clampValue(Number(box.height || 20), 20, 1000 - y);

      frame.dataset.previewX = String(Math.round(x));
      frame.dataset.previewY = String(Math.round(y));
      frame.dataset.previewWidth = String(Math.round(width));
      frame.dataset.previewHeight = String(Math.round(height));
      frame.style.left = `${x / 10}%`;
      frame.style.top = `${y / 10}%`;
      frame.style.width = `${width / 10}%`;
      frame.style.height = `${height / 10}%`;
    }

    updatePreviewReadout(frame, box) {
      const readout = frame ? frame.querySelector('[data-guide-preview-readout]') : null;
      if (!readout || !box) return;
      readout.textContent = `X ${box.x} · Y ${box.y} · W ${box.width} · H ${box.height}`;
    }

    requestPreviewLayouts(target) {
      if (this.previewLayoutFrame) window.cancelAnimationFrame(this.previewLayoutFrame);

      this.previewLayoutFrame = window.requestAnimationFrame(() => {
        this.previewLayoutFrame = null;

        if (target && target.matches && target.matches('[data-guide-preview-frame]')) {
          this.layoutPreviewFrame(target);
          return;
        }

        const scope = target && target.querySelectorAll ? target : this.root;
        scope.querySelectorAll('[data-guide-preview-frame]').forEach((frame) => {
          const step = frame.closest('[data-guide-step]');
          if (step && !step.hidden) this.layoutPreviewFrame(frame);
        });
      });
    }

    layoutPreviewFrame(frame) {
      if (!frame || frame.offsetParent === null) return;

      const preview = frame.querySelector('[data-guide-preview]');
      if (!preview) return;

      const width = frame.clientWidth;
      const height = frame.clientHeight;
      if (!width || !height) return;

      const actualText = preview.textContent || '';
      const referenceText = this.getPreviewReferenceText(preview) || actualText || 'M';
      const scale = this.clampValue(Number(preview.dataset.previewFontScale || 100), 10, 100) / 100;

      const fullDefaultSize = this.findPreviewFitSize(preview, referenceText, width, height);
      const defaultSize = Math.max(1, fullDefaultSize * scale);
      let finalSize = defaultSize;

      if (!this.previewTextFits(preview, actualText || ' ', finalSize, width, height)) {
        finalSize = this.findPreviewFitSize(preview, actualText || ' ', width, height, defaultSize);
      }

      preview.style.fontSize = `${Math.max(1, finalSize).toFixed(2)}px`;
    }

    getPreviewReferenceText(preview) {
      if (!preview) return '';

      const source = preview.dataset.previewSource || '';
      const step = preview.closest('[data-guide-step]');

      if (source === 'manual') return preview.dataset.previewManual || '';

      if (source === 'current_input') {
        const input = step ? step.querySelector('[data-guide-input]') : null;
        return input ? (input.dataset.defaultValue || '') : '';
      }

      if (source === 'stored_key') {
        const key = preview.dataset.previewKey || 'guide_text';
        const input = Array.from(this.root.querySelectorAll('[data-guide-input]'))
          .find((item) => (item.dataset.fieldKey || 'guide_text') === key);
        return input ? (input.dataset.defaultValue || '') : '';
      }

      if (source === 'latest') {
        const latestKey = this.state.latestKey || '';
        if (latestKey) {
          const input = Array.from(this.root.querySelectorAll('[data-guide-input]'))
            .find((item) => (item.dataset.fieldKey || 'guide_text') === latestKey);
          if (input) return input.dataset.defaultValue || '';
        }

        const firstInput = this.root.querySelector('[data-guide-input]');
        return firstInput ? (firstInput.dataset.defaultValue || '') : '';
      }

      return '';
    }

    getPreviewMeasureNode() {
      if (this.previewMeasureNode && this.previewMeasureNode.isConnected) {
        return this.previewMeasureNode;
      }

      const node = document.createElement('span');
      node.setAttribute('aria-hidden', 'true');
      node.style.position = 'fixed';
      node.style.left = '-10000px';
      node.style.top = '-10000px';
      node.style.width = 'max-content';
      node.style.height = 'auto';
      node.style.maxWidth = 'none';
      node.style.visibility = 'hidden';
      node.style.pointerEvents = 'none';
      node.style.whiteSpace = 'nowrap';
      node.style.padding = '0';
      node.style.border = '0';
      node.style.margin = '0';
      document.body.appendChild(node);
      this.previewMeasureNode = node;
      return node;
    }

    measurePreviewText(preview, text, fontSize) {
      const node = this.getPreviewMeasureNode();
      const style = window.getComputedStyle(preview);

      node.style.fontFamily = style.fontFamily;
      node.style.fontWeight = style.fontWeight;
      node.style.fontStyle = style.fontStyle;
      node.style.fontVariant = style.fontVariant;
      node.style.letterSpacing = style.letterSpacing;
      node.style.textTransform = style.textTransform;
      node.style.lineHeight = preview.dataset.previewLineHeight || '1.2';
      node.style.fontSize = `${fontSize}px`;
      node.textContent = text || ' ';

      const rect = node.getBoundingClientRect();
      return { width: rect.width, height: rect.height };
    }

    previewTextFits(preview, text, fontSize, width, height) {
      const measured = this.measurePreviewText(preview, text, fontSize);
      return measured.width <= width + 0.25 && measured.height <= height + 0.25;
    }

    findPreviewFitSize(preview, text, width, height, upperLimit) {
      let low = 1;
      let high = upperLimit || Math.min(2000, Math.max(width, height) * 2);

      if (this.previewTextFits(preview, text, high, width, height)) return high;

      for (let index = 0; index < 12; index += 1) {
        const middle = (low + high) / 2;
        if (this.previewTextFits(preview, text, middle, width, height)) {
          low = middle;
        } else {
          high = middle;
        }
      }

      return low;
    }

    clampIndex(index) {
      if (!this.steps.length) return 0;
      return Math.min(Math.max(Number(index) || 0, 0), this.steps.length - 1);
    }

    showStep(index, options) {
      if (!this.steps.length) return;

      const settings = Object.assign({ focus: false, persist: true }, options || {});
      const previousStep = this.steps[this.currentIndex];
      const nextIndex = this.clampIndex(index);
      const activeStep = this.steps[nextIndex];

      if (previousStep && previousStep !== activeStep && !previousStep.hidden) {
        this.rememberPreviewBox(previousStep);
      }

      const keepPreviousMedia = Boolean(
        previousStep &&
        previousStep !== activeStep &&
        !previousStep.hidden
      );

      this.currentIndex = nextIndex;
      this.activationToken += 1;
      const token = this.activationToken;
      this.clearTimers();

      this.steps.forEach((step, stepIndex) => {
        const active = stepIndex === this.currentIndex;
        const backdrop = keepPreviousMedia && step === previousStep;

        this.clearTransitionBackdrop(step);
        this.resetMediaEntrance(step, active);

        if (active && this.isGifInteractBeforePlayStep(step)) {
          step.dataset.gifInteractionState = 'preparing';
        } else {
          step.removeAttribute('data-gif-interaction-state');
        }
        step.style.removeProperty('pointer-events');

        step.hidden = !(active || backdrop);
        step.classList.toggle('is-active', active);

        if (backdrop) {
          this.setTransitionBackdrop(step);
          return;
        }

        step.classList.remove('is-ready', 'is-gif-frozen', 'is-media-visible');

        if (active && keepPreviousMedia) {
          step.style.zIndex = '2';
        }

        const canvas = step.querySelector('[data-guide-gif-freeze]');
        if (canvas) {
          canvas.style.display = '';
          const context = canvas.getContext('2d');
          if (context) context.clearRect(0, 0, canvas.width, canvas.height);
        }

        if (active && keepPreviousMedia) {
          const mediaLayer = step.querySelector('.tib-guide__media-layer');
          if (mediaLayer) mediaLayer.style.opacity = '0';
        }
      });

      this.stepButtons.forEach((button, buttonIndex) => {
        const active = buttonIndex === this.currentIndex;
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-selected', active ? 'true' : 'false');
        // if (active && settings.focus && window.innerWidth < 750 && !this.designMode) {
        //   button.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
        // }
      });

      if (!this.designMode) {
        activeStep.querySelectorAll('[data-guide-preview-frame]').forEach((frame) => {
          this.applyPreviewPositionSource(frame);
        });
      }

      if (settings.persist) {
        this.state.currentStep = this.currentIndex;
        this.saveState();
      }

      this.updatePreviews();
      this.updateProgress();
      this.updateControls();
      this.scheduleInstructionLabelLayout(activeStep);
      this.prepareStep(activeStep, token);

      if (settings.focus) {
        activeStep.focus({ preventScroll: true });
      }
    }

    preloadMediaSequentially() {
      const generation = ++this.preloadGeneration;

      const run = async () => {
        for (const step of this.steps) {
          if (generation !== this.preloadGeneration || !this.root.isConnected) return;
          const media = step.querySelector('[data-guide-media]');
          if (!media) continue;

          await this.ensureMediaPreloaded(step, media);
          if (generation !== this.preloadGeneration || !this.root.isConnected) return;
        }
      };

      run().catch(() => {});
    }

    ensureMediaPreloaded(step, media) {
      if (!step || !media || media.dataset.guidePreloaded === 'true') return Promise.resolve();
      if (this.mediaLoadPromises.has(media)) return this.mediaLoadPromises.get(media);

      const promise = (async () => {
        const source = media.dataset.mediaSrc || media.currentSrc || media.src;
        if (!source) return;

        if (step.dataset.mediaKind === 'gif') {
          const asset = await this.getGifAsset(source);

          if (!asset.blob) {
            if (!media.getAttribute('src')) {
              await this.loadMediaSource(media, source);
            } else {
              await this.waitForImage(media);
            }
          }
        } else {
          if (!media.getAttribute('src')) {
            await this.loadMediaSource(media, source);
          } else {
            await this.waitForImage(media);
          }

          if (typeof media.decode === 'function') {
            try {
              await media.decode();
            } catch (error) {
              // Loading successfully is enough when decode() is unavailable or rejects.
            }
          }
        }

        media.dataset.guidePreloaded = 'true';
      })().finally(() => {
        this.mediaLoadPromises.delete(media);
      });

      this.mediaLoadPromises.set(media, promise);
      return promise;
    }

    getGifAsset(source) {
      if (!source) return Promise.resolve({ blob: null, timing: null });
      if (this.gifAssetPromises.has(source)) return this.gifAssetPromises.get(source);

      const promise = (async () => {
        try {
          const response = await fetch(source, { cache: 'force-cache', credentials: 'omit' });
          if (!response.ok) throw new Error(`GIF request failed: ${response.status}`);

          const buffer = await response.arrayBuffer();
          const contentType = response.headers.get('content-type') || 'image/gif';
          const singleRunBuffer = this.stripGifLoopExtension(buffer);
          return {
            blob: new Blob([buffer], { type: contentType }),
            singleRunBlob: new Blob([singleRunBuffer], { type: contentType }),
            timing: this.parseGifTiming(buffer)
          };
        } catch (error) {
          return { blob: null, timing: null };
        }
      })();

      this.gifAssetPromises.set(source, promise);
      return promise;
    }

    stripGifLoopExtension(buffer) {
      try {
        const bytes = new Uint8Array(buffer);
        if (bytes.length < 13) return buffer;
        if (String.fromCharCode(bytes[0], bytes[1], bytes[2]) !== 'GIF') return buffer;

        let offset = 13;
        const globalPacked = bytes[10];
        if (globalPacked & 0x80) {
          offset += 3 * (1 << ((globalPacked & 0x07) + 1));
        }
        if (offset > bytes.length) return buffer;

        const parts = [bytes.slice(0, offset)];
        let removedLoopExtension = false;

        while (offset < bytes.length) {
          const blockStart = offset;
          const marker = bytes[offset++];

          if (marker === 0x3B) {
            parts.push(bytes.slice(blockStart));
            break;
          }

          if (marker === 0x21) {
            if (offset + 1 >= bytes.length) return buffer;
            const label = bytes[offset++];
            const blockSize = bytes[offset++];
            if (offset + blockSize > bytes.length) return buffer;

            let applicationId = '';
            if (label === 0xFF && blockSize === 11) {
              applicationId = String.fromCharCode.apply(null, bytes.slice(offset, offset + blockSize));
            }

            offset += blockSize;
            while (offset < bytes.length) {
              const size = bytes[offset++];
              if (size === 0) break;
              offset += size;
              if (offset > bytes.length) return buffer;
            }

            const isLoopExtension = label === 0xFF &&
              (applicationId === 'NETSCAPE2.0' || applicationId === 'ANIMEXTS1.0');

            if (isLoopExtension) {
              removedLoopExtension = true;
            } else {
              parts.push(bytes.slice(blockStart, offset));
            }
            continue;
          }

          if (marker === 0x2C) {
            if (offset + 9 > bytes.length) return buffer;
            const localPacked = bytes[offset + 8];
            offset += 9;

            if (localPacked & 0x80) {
              offset += 3 * (1 << ((localPacked & 0x07) + 1));
              if (offset > bytes.length) return buffer;
            }

            if (offset >= bytes.length) return buffer;
            offset += 1;
            while (offset < bytes.length) {
              const size = bytes[offset++];
              if (size === 0) break;
              offset += size;
              if (offset > bytes.length) return buffer;
            }

            parts.push(bytes.slice(blockStart, offset));
            continue;
          }

          return buffer;
        }

        if (!removedLoopExtension) return buffer;

        const totalLength = parts.reduce((total, part) => total + part.length, 0);
        const output = new Uint8Array(totalLength);
        let writeOffset = 0;
        parts.forEach((part) => {
          output.set(part, writeOffset);
          writeOffset += part.length;
        });
        return output.buffer;
      } catch (error) {
        return buffer;
      }
    }

    parseGifTiming(buffer) {
      try {
        const bytes = new Uint8Array(buffer);
        if (bytes.length < 13) return null;
        if (String.fromCharCode(bytes[0], bytes[1], bytes[2]) !== 'GIF') return null;

        let offset = 13;
        const globalPacked = bytes[10];
        if (globalPacked & 0x80) {
          offset += 3 * (1 << ((globalPacked & 0x07) + 1));
        }

        const frameDelays = [];
        let pendingDelay = 100;

        while (offset < bytes.length) {
          const marker = bytes[offset++];
          if (marker === 0x3B) break;

          if (marker === 0x21) {
            if (offset >= bytes.length) break;
            const label = bytes[offset++];
            if (offset >= bytes.length) break;
            const blockSize = bytes[offset++];

            if (label === 0xF9 && blockSize >= 4 && offset + blockSize <= bytes.length) {
              const delayCs = bytes[offset + 1] | (bytes[offset + 2] << 8);
              const delayMs = delayCs * 10;
              pendingDelay = delayMs === 0 ? 100 : Math.max(20, delayMs);
            }

            offset += blockSize;
            while (offset < bytes.length) {
              const size = bytes[offset++];
              if (size === 0) break;
              offset += size;
            }
            continue;
          }

          if (marker === 0x2C) {
            if (offset + 9 > bytes.length) break;
            const localPacked = bytes[offset + 8];
            offset += 9;

            if (localPacked & 0x80) {
              offset += 3 * (1 << ((localPacked & 0x07) + 1));
            }

            if (offset >= bytes.length) break;
            offset += 1;
            while (offset < bytes.length) {
              const size = bytes[offset++];
              if (size === 0) break;
              offset += size;
            }

            frameDelays.push(pendingDelay);
            pendingDelay = 100;
            continue;
          }

          break;
        }

        if (!frameDelays.length) return null;
        const totalDuration = frameDelays.reduce((total, delay) => total + delay, 0);
        const lastFrameDuration = frameDelays[frameDelays.length - 1];
        if (!totalDuration || !lastFrameDuration) return null;

        return {
          totalDuration,
          lastFrameDuration,
          sampleAt: Math.max(0, totalDuration - (lastFrameDuration / 2))
        };
      } catch (error) {
        return null;
      }
    }

    async prepareStep(step, token) {
      const mediaKind = step.dataset.mediaKind;
      const interactBeforePlay = this.isGifInteractBeforePlayStep(step);
      let media = step.querySelector('[data-guide-media]');

      if (mediaKind === 'gif' && media) {
        await this.ensureMediaPreloaded(step, media);
        if (token !== this.activationToken) return;

        media = step.querySelector('[data-guide-media]');
        const source = media.dataset.mediaSrc || media.currentSrc || media.src;
        const asset = await this.getGifAsset(source);
        if (token !== this.activationToken) return;

        if (interactBeforePlay) {
          const run = await this.restartGif(media, asset.singleRunBlob || asset.blob);
          if (token !== this.activationToken) return;

          this.freezeGif(step, run.media);
          await this.revealStepMedia(step, token);
          if (token !== this.activationToken) return;
        } else {
          const replayEnabled = step.dataset.gifReplay === 'true';
          const replayDelay = Math.max(0, Number(step.dataset.gifReplayDelay || 0));
          const run = await this.restartGif(media, asset.singleRunBlob || asset.blob);
          if (token !== this.activationToken) return;

          await this.revealStepMedia(step, token);
          if (token !== this.activationToken) return;

          if (this.designMode) {
            this.finishGifCycle(step, run, token, asset.timing).then((finished) => {
              if (!finished || token !== this.activationToken || !replayEnabled) return;
              this.replayGif(step, token, asset, replayDelay).catch(() => {});
            }).catch(() => {});
          } else {
            const finished = await this.finishGifCycle(step, run, token, asset.timing);
            if (!finished || token !== this.activationToken) return;

            if (replayEnabled) {
              this.replayGif(step, token, asset, replayDelay).catch(() => {});
            }
          }
        }
      } else if (media) {
        await this.ensureMediaPreloaded(step, media);
        if (token !== this.activationToken) return;
        await this.revealStepMedia(step, token);
      } else {
        await this.revealStepMedia(step, token);
      }

      if (token !== this.activationToken) return;
      const delay = this.designMode ? 0 : Number(step.dataset.overlayDelay || 0);
      await this.wait(delay);
      if (token !== this.activationToken) return;

      if (mediaKind === 'gif') {
        await this.waitForInstructionLabelFonts(step);
        if (token !== this.activationToken) return;
      }

      this.clearInstructionLabelLayoutTimers();
      this.positionInstructionLabels(step);
      step.classList.add('is-ready');
      this.requestPreviewLayouts(step);

      if (interactBeforePlay) {
        step.dataset.gifInteractionState = 'awaiting';
        this.updateControls();
      }

      if (mediaKind !== 'gif') {
        this.requestInstructionLabelLayout(step);
      }
    }

    isGifInteractBeforePlayStep(step) {
      return Boolean(
        step &&
        step.dataset.mediaKind === 'gif' &&
        step.dataset.gifInteractBeforePlay === 'true'
      );
    }

    async playGifAfterInteraction(step, token) {
      if (
        !this.isGifInteractBeforePlayStep(step) ||
        step.dataset.gifInteractionState !== 'awaiting'
      ) return;

      step.dataset.gifInteractionState = 'running';
      step.style.pointerEvents = 'none';
      this.clearInstructionLabelLayoutTimers();
      step.classList.remove('is-ready');
      this.updateControls();

      let media = step.querySelector('[data-guide-media]');
      if (!media) {
        step.dataset.gifInteractionState = 'done';
        step.style.removeProperty('pointer-events');
        this.updateControls();
        this.next(true);
        return;
      }

      const source = media.dataset.mediaSrc || media.currentSrc || media.src;
      const asset = await this.getGifAsset(source);
      if (token !== this.activationToken || step !== this.steps[this.currentIndex]) return;

      const run = await this.restartGif(media, asset.singleRunBlob || asset.blob);
      if (token !== this.activationToken || step !== this.steps[this.currentIndex]) return;

      step.classList.remove('is-gif-frozen');

      const finished = await this.finishGifCycle(step, run, token, asset.timing);
      if (!finished || token !== this.activationToken || step !== this.steps[this.currentIndex]) return;

      step.dataset.gifInteractionState = 'done';
      step.style.removeProperty('pointer-events');
      this.updateControls();
      this.next(true);
    }

    getMediaEntranceStart(step) {
      const transition = step ? (step.dataset.mediaTransition || 'none') : 'none';
      const start = { opacity: '0', transform: 'none' };

      if (transition === 'fade-up') start.transform = 'translateY(18px)';
      if (transition === 'fade-down') start.transform = 'translateY(-18px)';
      if (transition === 'slide-left') start.transform = 'translateX(-28px)';
      if (transition === 'slide-right') start.transform = 'translateX(28px)';
      if (transition === 'zoom') start.transform = 'scale(.97)';

      return start;
    }

    resetMediaEntrance(step, active) {
      if (!step) return;

      const mediaLayer = step.querySelector('.tib-guide__media-layer');
      if (!mediaLayer) return;

      mediaLayer.getAnimations().forEach((animation) => animation.cancel());

      const transition = step.dataset.mediaTransition || 'none';
      const duration = Math.max(0, Number(step.dataset.mediaTransitionDuration || 0));

      if (active && transition !== 'none' && duration > 0) {
        const start = this.getMediaEntranceStart(step);
        mediaLayer.style.opacity = start.opacity;
        mediaLayer.style.transform = start.transform;
      } else {
        mediaLayer.style.opacity = '1';
        mediaLayer.style.transform = 'none';
      }
    }

    setTransitionBackdrop(step) {
      if (!step) return;

      step.classList.add('is-transition-backdrop');
      step.setAttribute('aria-hidden', 'true');
      step.style.zIndex = '1';
      step.style.pointerEvents = 'none';

      Array.from(step.children).forEach((child) => {
        if (child.classList.contains('tib-guide__media-layer')) return;
        child.dataset.guideTransitionHidden = 'true';
        child.style.visibility = 'hidden';
      });

      const mediaLayer = step.querySelector('.tib-guide__media-layer');
      if (mediaLayer) {
        mediaLayer.style.opacity = '1';
        mediaLayer.style.transform = 'none';
      }
    }

    clearTransitionBackdrop(step) {
      if (!step) return;

      step.classList.remove('is-transition-backdrop');
      step.removeAttribute('aria-hidden');
      step.style.removeProperty('z-index');
      step.style.removeProperty('pointer-events');

      step.querySelectorAll('[data-guide-transition-hidden="true"]').forEach((element) => {
        element.style.removeProperty('visibility');
        element.removeAttribute('data-guide-transition-hidden');
      });
    }

    completeMediaSwap(activeStep) {
      if (!activeStep || activeStep !== this.steps[this.currentIndex]) return;

      this.steps.forEach((step) => {
        if (step === activeStep) {
          this.clearTransitionBackdrop(step);
          step.hidden = false;
          return;
        }

        if (step.classList.contains('is-transition-backdrop')) {
          this.clearTransitionBackdrop(step);
          step.hidden = true;
        }
      });
    }

    async revealStepMedia(step, token) {
      if (!step) return;

      const mediaLayer = step.querySelector('.tib-guide__media-layer');
      if (!mediaLayer) {
        this.completeMediaSwap(step);
        return;
      }

      const transition = step.dataset.mediaTransition || 'none';
      const duration = Math.max(0, Number(step.dataset.mediaTransitionDuration || 0));

      if (transition === 'none' || !duration || typeof mediaLayer.animate !== 'function') {
        mediaLayer.style.opacity = '1';
        mediaLayer.style.transform = 'none';
        this.completeMediaSwap(step);
        return;
      }

      mediaLayer.getAnimations().forEach((animation) => animation.cancel());
      const from = this.getMediaEntranceStart(step);
      mediaLayer.style.opacity = from.opacity;
      mediaLayer.style.transform = from.transform;

      await new Promise((resolve) => window.requestAnimationFrame(() => window.requestAnimationFrame(resolve)));
      if (token !== this.activationToken || step !== this.steps[this.currentIndex]) return;

      const animation = mediaLayer.animate(
        [
          { opacity: Number(from.opacity), transform: from.transform },
          { opacity: 1, transform: 'none' }
        ],
        {
          duration,
          easing: 'cubic-bezier(.2, .7, .2, 1)',
          fill: 'forwards'
        }
      );

      try {
        await animation.finished;
      } catch (error) {
        return;
      }

      if (token !== this.activationToken || step !== this.steps[this.currentIndex]) return;
      mediaLayer.style.opacity = '1';
      mediaLayer.style.transform = 'none';
      animation.cancel();
      this.completeMediaSwap(step);
    }

    async finishGifCycle(step, run, token, timing) {
      if (!step || !run || !run.media) return false;

      const fallbackDuration = 5000;
      const totalDuration = timing && timing.totalDuration ? timing.totalDuration : fallbackDuration;
      const endWait = Math.max(0, run.startedAt + totalDuration - performance.now());
      await this.wait(endWait + 34);
      return token === this.activationToken && step === this.steps[this.currentIndex];
    }

    async replayGif(step, token, asset, replayDelay) {
      while (token === this.activationToken && step === this.steps[this.currentIndex]) {
        await this.wait(replayDelay);
        if (token !== this.activationToken || step !== this.steps[this.currentIndex]) return;

        const media = step.querySelector('[data-guide-media]');
        if (!media) return;

        step.classList.remove('is-gif-frozen');
        const run = await this.restartGif(media, asset.singleRunBlob || asset.blob);
        if (token !== this.activationToken || step !== this.steps[this.currentIndex]) return;

        const finished = await this.finishGifCycle(step, run, token, asset.timing);
        if (!finished) return;
      }
    }

    async restartGif(media, blob) {
      const source = media.dataset.mediaSrc || media.currentSrc || media.src;
      const oldObjectUrl = media.dataset.guideObjectUrl || '';
      const freshMedia = media.cloneNode(false);

      freshMedia.removeAttribute('src');
      freshMedia.dataset.mediaSrc = source;
      freshMedia.removeAttribute('data-guide-object-url');
      freshMedia.loading = 'eager';
      freshMedia.decoding = 'async';
      media.replaceWith(freshMedia);

      if (oldObjectUrl) this.revokeObjectUrl(oldObjectUrl);

      let runSource;
      if (blob) {
        runSource = this.createObjectUrl(blob);
        freshMedia.dataset.guideObjectUrl = runSource;
      } else {
        const hashlessSource = source.split('#')[0];
        runSource = `${hashlessSource}#tib-guide-run-${++this.gifRunCounter}`;
      }

      const startedAt = await this.loadMediaSource(freshMedia, runSource);
      freshMedia.dataset.guidePreloaded = 'true';
      return { media: freshMedia, startedAt };
    }

    createObjectUrl(blob) {
      const url = URL.createObjectURL(blob);
      this.objectUrls.add(url);
      return url;
    }

    revokeObjectUrl(url) {
      if (!url || !this.objectUrls.has(url)) return;
      try {
        URL.revokeObjectURL(url);
      } catch (error) {
        // Ignore URLs already released by the browser.
      }
      this.objectUrls.delete(url);
    }

    loadMediaSource(image, source) {
      return new Promise((resolve) => {
        let settled = false;
        const finish = () => {
          if (settled) return;
          settled = true;
          image.removeEventListener('load', finish);
          image.removeEventListener('error', finish);
          resolve(performance.now());
        };

        image.addEventListener('load', finish);
        image.addEventListener('error', finish);
        image.src = source;

        if (image.complete) {
          Promise.resolve().then(finish);
        }
      });
    }

    waitForImage(image) {
      if (!image || image.complete) return Promise.resolve();
      return new Promise((resolve) => {
        const finish = () => resolve();
        image.addEventListener('load', finish, { once: true });
        image.addEventListener('error', finish, { once: true });
      });
    }

    freezeGif(step, image) {
      const canvas = step.querySelector('[data-guide-gif-freeze]');
      if (!canvas || !image) return;

      const rect = image.getBoundingClientRect();
      const scale = Math.min(window.devicePixelRatio || 1, 2);
      const width = Math.max(1, Math.round(rect.width * scale));
      const height = Math.max(1, Math.round(rect.height * scale));
      canvas.width = width;
      canvas.height = height;

      try {
        const context = canvas.getContext('2d');
        context.drawImage(image, 0, 0, width, height);
        step.classList.add('is-gif-frozen');
      } catch (error) {
        step.classList.remove('is-gif-frozen');
      }
    }

    async waitForInstructionLabelFonts(step) {
      if (
        !step ||
        !document.fonts ||
        typeof document.fonts.load !== 'function'
      ) return;

      const labels = Array.from(
        step.querySelectorAll('[data-guide-instruction-label] .tib-guide__label')
      );
      if (!labels.length) return;

      await Promise.all(labels.map((label) => {
        const style = window.getComputedStyle(label);
        const font = `${style.fontWeight || '400'} ${style.fontSize || '14px'} ${style.fontFamily || 'sans-serif'}`;

        try {
          return document.fonts.load(font, label.textContent || 'Text');
        } catch (error) {
          return Promise.resolve();
        }
      }));

      try {
        await document.fonts.ready;
      } catch (error) {
        // Continue with the current font when the browser cannot resolve font readiness.
      }

      await new Promise((resolve) => {
        window.requestAnimationFrame(() => window.requestAnimationFrame(resolve));
      });
    }

    clearInstructionLabelLayoutTimers() {
      this.labelLayoutTimers.forEach((timer) => window.clearTimeout(timer));
      this.labelLayoutTimers.clear();
    }

    scheduleInstructionLabelLayout(step) {
      const targetStep = step || this.steps[this.currentIndex];
      if (!targetStep) return;

      this.clearInstructionLabelLayoutTimers();

      const run = () => {
        if (
          !this.root.isConnected ||
          targetStep.hidden ||
          targetStep !== this.steps[this.currentIndex] ||
          targetStep.classList.contains('is-ready')
        ) return;
        this.requestInstructionLabelLayout(targetStep);
      };

      run();

      [80, 240, 600].forEach((delay) => {
        const timer = window.setTimeout(() => {
          this.labelLayoutTimers.delete(timer);
          run();
        }, delay);
        this.labelLayoutTimers.add(timer);
      });

      if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(run).catch(() => {});
      }
    }

    requestInstructionLabelLayout(step) {
      if (this.labelLayoutFrame) window.cancelAnimationFrame(this.labelLayoutFrame);
      this.labelLayoutFrame = window.requestAnimationFrame(() => {
        this.labelLayoutFrame = window.requestAnimationFrame(() => {
          this.labelLayoutFrame = null;
          this.positionInstructionLabels(step || this.steps[this.currentIndex]);
        });
      });
    }

    positionInstructionLabels(step) {
      if (!step || !this.stage || step.hidden) return;

      const stageRect = this.stage.getBoundingClientRect();
      if (!stageRect.width || !stageRect.height) return;

      step.querySelectorAll('[data-guide-instruction-label]').forEach((positioner) => {
        const label = positioner.querySelector('.tib-guide__label');
        const target = this.getInstructionLabelTarget(step, positioner.dataset.labelTarget);
        if (!label || !target) return;

        positioner.classList.remove('is-calculated');
        positioner.style.left = '0px';
        positioner.style.top = '0px';

        const measuredLabelRect = label.getBoundingClientRect();
        const labelRect = {
          width: label.offsetWidth || measuredLabelRect.width,
          height: label.offsetHeight || measuredLabelRect.height
        };
        const targetRect = target.getBoundingClientRect();
        const targetBox = {
          left: targetRect.left - stageRect.left,
          right: targetRect.right - stageRect.left,
          top: targetRect.top - stageRect.top,
          bottom: targetRect.bottom - stageRect.top,
          width: targetRect.width,
          height: targetRect.height
        };

        const requestedPosition = positioner.dataset.labelPosition || 'right';
        const resolvedPosition = positioner.dataset.labelAuto === 'true'
          ? this.chooseInstructionLabelPosition(stageRect, targetBox, labelRect)
          : requestedPosition;
        const anchor = this.calculateInstructionLabelAnchor(resolvedPosition, targetBox);

        positioner.dataset.resolvedPosition = resolvedPosition;
        positioner.style.left = `${anchor.left}px`;
        positioner.style.top = `${anchor.top}px`;
        positioner.classList.add('is-calculated');

        const padding = 8;
        let renderedRect = positioner.getBoundingClientRect();
        let correctionX = 0;
        let correctionY = 0;

        if (renderedRect.left < stageRect.left + padding) {
          correctionX = stageRect.left + padding - renderedRect.left;
        } else if (renderedRect.right > stageRect.right - padding) {
          correctionX = stageRect.right - padding - renderedRect.right;
        }

        if (renderedRect.top < stageRect.top + padding) {
          correctionY = stageRect.top + padding - renderedRect.top;
        } else if (renderedRect.bottom > stageRect.bottom - padding) {
          correctionY = stageRect.bottom - padding - renderedRect.bottom;
        }

        if (correctionX || correctionY) {
          positioner.style.left = `${anchor.left + correctionX}px`;
          positioner.style.top = `${anchor.top + correctionY}px`;
          renderedRect = positioner.getBoundingClientRect();
        }

        const targetCenterX = targetRect.left + targetRect.width / 2;
        const targetCenterY = targetRect.top + targetRect.height / 2;
        const verticalArrow = resolvedPosition === 'left' || resolvedPosition === 'right';
        const rawArrowOffset = verticalArrow
          ? targetCenterY - renderedRect.top
          : targetCenterX - renderedRect.left;
        const labelDimension = verticalArrow ? renderedRect.height : renderedRect.width;
        const arrowOffset = this.clampValue(rawArrowOffset, 16, Math.max(16, labelDimension - 16));

        positioner.style.setProperty('--tib-label-arrow-offset', `${arrowOffset}px`);
      });
    }

    getInstructionLabelTarget(step, targetType) {
      if (targetType === 'input') return step.querySelector('[data-guide-input]');
      return step.querySelector('.tib-guide__hotspot--1 .tib-guide__hotspot-visual')
        || step.querySelector('.tib-guide__hotspot--1');
    }

    chooseInstructionLabelPosition(stageRect, target, labelRect) {
      const gap = 14;
      const candidates = [
        { position: 'right', space: stageRect.width - target.right - gap - labelRect.width },
        { position: 'left', space: target.left - gap - labelRect.width },
        { position: 'bottom', space: stageRect.height - target.bottom - gap - labelRect.height },
        { position: 'top', space: target.top - gap - labelRect.height }
      ];
      candidates.sort((a, b) => b.space - a.space);
      return candidates[0].position;
    }

    calculateInstructionLabelAnchor(position, target) {
      const gap = 14;
      const targetCenterX = target.left + target.width / 2;
      const targetCenterY = target.top + target.height / 2;

      if (position === 'top') {
        return { left: targetCenterX, top: target.top - gap };
      }
      if (position === 'bottom') {
        return { left: targetCenterX, top: target.bottom + gap };
      }
      if (position === 'left') {
        return { left: target.left - gap, top: targetCenterY };
      }
      return { left: target.right + gap, top: targetCenterY };
    }

    clampValue(value, minimum, maximum) {
      return Math.min(Math.max(value, minimum), maximum);
    }

    wait(duration) {
      if (!duration) return Promise.resolve();
      return new Promise((resolve) => {
        const timer = window.setTimeout(() => {
          this.timers.delete(timer);
          resolve();
        }, duration);
        this.timers.set(timer, resolve);
      });
    }

    clearTimers() {
      this.timers.forEach((resolve, timer) => {
        window.clearTimeout(timer);
        resolve();
      });
      this.timers.clear();
    }

    updateProgress() {
      const current = this.currentIndex + 1;
      const total = this.steps.length;
      const percentage = total ? (current / total) * 100 : 0;

      if (this.currentLabel) this.currentLabel.textContent = String(current);
      if (this.totalLabel) this.totalLabel.textContent = String(total);
      if (this.progress) this.progress.style.width = `${percentage}%`;
      this.root.classList.toggle('is-last-step', total > 0 && this.currentIndex === total - 1);
    }

    isLastStep() {
      return this.steps.length > 0 && this.currentIndex === this.steps.length - 1;
    }

    updateControls() {
      const step = this.steps[this.currentIndex];
      const input = step ? step.querySelector('[data-guide-input]') : null;
      const effectiveInputValue = input ? this.getInputEffectiveValue(input, false) : '';
      const inputInvalid = Boolean(input && input.required && !effectiveInputValue.trim());
      const footerAllowed = !step || step.dataset.allowFooterNext !== 'false';
      const isFirst = this.currentIndex === 0;
      const isLast = this.isLastStep();
      const gifState = step ? step.dataset.gifInteractionState : '';
      const gifBusy = gifState === 'preparing' || gifState === 'running';
      const nextDisabled = !footerAllowed || inputInvalid || gifBusy;

      if (this.previousButton) this.previousButton.disabled = isFirst;
      if (this.nextButton) {
        this.nextButton.disabled = isLast ? gifBusy : nextDisabled;
        this.nextButton.setAttribute('aria-label', isLast ? 'Restart guide' : 'Next step');
        this.nextButton.setAttribute('title', isLast ? 'Restart guide' : 'Next step');
      }

      if (this.browserPreviousButton) this.browserPreviousButton.disabled = isFirst;
      if (this.browserNextButton) this.browserNextButton.disabled = isLast || nextDisabled;
      if (this.browserRestartButton) this.browserRestartButton.disabled = !this.steps.length;
      if (this.browserCloseButton) this.browserCloseButton.disabled = !this.isBrowserFullscreen();
    }

    next(skipGifInteraction) {
      const step = this.steps[this.currentIndex];
      const input = step ? step.querySelector('[data-guide-input]') : null;

      if (input) {
        this.storeInputValue(input, true);
      }

      if (input && input.required && !input.value.trim()) {
        input.reportValidity();
        input.focus();
        return;
      }

      if (!skipGifInteraction && this.isGifInteractBeforePlayStep(step)) {
        const gifState = step.dataset.gifInteractionState;

        if (gifState === 'awaiting') {
          this.playGifAfterInteraction(step, this.activationToken).catch(() => {});
        }
        return;
      }

      if (this.currentIndex >= this.steps.length - 1) return;
      this.showStep(this.currentIndex + 1, { focus: true });
    }

    previous() {
      if (this.currentIndex <= 0) return;
      this.showStep(this.currentIndex - 1, { focus: true });
    }

    restart() {
      this.state.currentStep = 0;
      this.saveState();
      this.showStep(0, { focus: true });
    }

    selectBlock(blockId) {
      const index = this.steps.findIndex((step) => step.dataset.blockId === blockId);
      if (index !== -1) this.showStep(index, { focus: false, persist: false });
    }

    destroy() {
      this.activationToken += 1;
      this.preloadGeneration += 1;
      this.clearTimers();
      if (this.labelLayoutFrame) {
        window.cancelAnimationFrame(this.labelLayoutFrame);
        this.labelLayoutFrame = null;
      }
      if (this.previewLayoutFrame) {
        window.cancelAnimationFrame(this.previewLayoutFrame);
        this.previewLayoutFrame = null;
      }
      if (this.previewMeasureNode) {
        this.previewMeasureNode.remove();
        this.previewMeasureNode = null;
      }
      this.clearInstructionLabelLayoutTimers();
      if (this.copyFeedbackTimer) {
        window.clearTimeout(this.copyFeedbackTimer);
        this.copyFeedbackTimer = null;
      }
      if (this.fallbackFullscreen) this.setFallbackFullscreen(false);
      this.objectUrls.forEach((url) => {
        try { URL.revokeObjectURL(url); } catch (error) {}
      });
      this.objectUrls.clear();
      this.cleanups.forEach((cleanup) => cleanup());
      this.cleanups = [];
      this.root.removeAttribute('data-tib-guide-initialized');
    }
  }

  function initRoot(root) {
    if (!root || root.dataset.tibGuideInitialized === 'true') return;
    root.dataset.tibGuideInitialized = 'true';
    const instance = new TIBGuidePlayer(root);
    registry.set(root.dataset.sectionId, instance);
  }

  function initAll(scope) {
    const target = scope || document;
    if (target.matches && target.matches('[data-tib-guide-player]')) initRoot(target);
    target.querySelectorAll('[data-tib-guide-player]').forEach(initRoot);
  }

  function boot() {
    initAll(document);

    document.addEventListener('shopify:section:load', (event) => {
      initAll(event.target);
    });

    document.addEventListener('shopify:section:unload', (event) => {
      const sectionId = event.detail && event.detail.sectionId;
      const instance = registry.get(sectionId);
      if (!instance) return;
      instance.destroy();
      registry.delete(sectionId);
    });

    document.addEventListener('shopify:block:select', (event) => {
      const sectionId = event.detail && event.detail.sectionId;
      const blockId = event.detail && event.detail.blockId;
      const instance = registry.get(sectionId);
      if (instance && blockId) instance.selectBlock(blockId);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }
})();
